import type { DocumentSession } from '../core/session';
import { listRecoveries, saveRecovery } from './recovery';

interface RecoveryReloadOperations {
  save: typeof saveRecovery;
  list: typeof listRecoveries;
}

/** Verify every currently dirty document, then recheck after the reload confirmation. */
export async function prepareRecoveryReload(
  readSessions: () => readonly DocumentSession[],
  operations: RecoveryReloadOperations = { save: saveRecovery, list: listRecoveries },
): Promise<{ dirtyCount: number; isCurrent: () => boolean; verify: () => Promise<boolean> }> {
  const sessions = [...readSessions()];
  if (sessions.some(session => session.history.hasLiveCommand)) throw new Error('Finish the active edit before reloading.');
  const dirty = sessions.filter(session => session.dirty);
  const bindings = new Map(dirty.map(session => [session.sessionId, {
    session, revision: session.currentRevision, documentId: session.document.id,
  }]));
  const isCurrent = () => readSessions().every(session => {
    if (session.history.hasLiveCommand) return false;
    if (!session.dirty) return true;
    const binding = bindings.get(session.sessionId);
    return binding?.session === session && binding.documentId === session.document.id
      && session.isCurrent({ sessionId: session.sessionId, revision: binding.revision });
  });
  const verify = async () => {
    if (!isCurrent()) return false;
    if (readSessions().every(session => !session.dirty)) return true;
    const recoveries = await operations.list();
    if (!isCurrent()) return false;
    return readSessions().filter(session => session.dirty).every(session => recoveries.some(record =>
      record.sessionId === session.sessionId && record.documentId === session.document.id
      && record.revision === session.currentRevision && record.generation === 'current'));
  };
  await Promise.all(dirty.map(session => operations.save(session)));
  if (!await verify()) throw new Error('Documents changed or recovery is no longer available. Save your edits before reloading.');
  return { dirtyCount: dirty.length, isCurrent, verify };
}
